import React, { useEffect, useMemo, useState } from "react";
import api, { API, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n, bilingual } from "../lib/i18n";
import { PencilSimple, X, Plus, Trash } from "@phosphor-icons/react";
import { uniqueRanches, repRanchId } from "../lib/ranchUtils";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}
function fromLocalInput(v) {
  return v ? new Date(v).toISOString() : null;
}

export default function Timesheets() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const canEdit = ["admin", "supervisor"].includes(user?.role);
  const [entries, setEntries] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(weekAgo);
  const [end, setEnd] = useState(today);
  const [ranches, setRanches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState("employee"); // "employee" | "equipment"
  const [equipRows, setEquipRows] = useState([]);
  const [equipTotal, setEquipTotal] = useState(0);
  const [blockData, setBlockData] = useState({ rows: [], total_hours: 0, entry_count: 0 });
  const [blockRanch, setBlockRanch] = useState("");

  const loadBlocks = async (rid = blockRanch) => {
    const { data } = await api.get(`/reports/block-hours?start_date=${start}&end_date=${end}${rid ? `&ranch_id=${rid}` : ""}`);
    setBlockData(data);
  };

  const downloadBlockXlsx = async () => {
    const token = getToken();
    const res = await fetch(`${API}/export/block-hours?start_date=${start}&end_date=${end}${blockRanch ? `&ranch_id=${blockRanch}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = `block_hours_${start}_${end}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  // edit modal
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/time-entries?start_date=${start}&end_date=${end}`);
    setEntries(data);
  };
  const loadEquipment = async () => {
    try {
      const { data } = await api.get(`/reports/equipment?start_date=${start}&end_date=${end}`);
      setEquipRows(data.rows || []);
      setEquipTotal(data.total_equipment_hours || 0);
    } catch { /* ignore */ }
  };
  const loadActive = () => (view === "equipment" ? loadEquipment() : view === "block" ? loadBlocks() : load());

  const removeEntry = async (e) => {
    if (!window.confirm(`Delete ${e.employee_name}'s entry on ${e.work_date}? This cannot be undone.`)) return;
    try {
      await api.delete(`/time-entries/${e.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not delete entry");
    }
  };
  useEffect(() => {
    load();
    (async () => {
      try {
        const [r, j] = await Promise.all([api.get("/ranches"), api.get("/jobcodes")]);
        setRanches(r.data);
        setJobs(j.data);
      } catch { /* ignore */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = useMemo(() => {
    const map = new Map();
    jobs.forEach((j) => {
      const name = j.category || "Uncategorized";
      if (!map.has(name)) map.set(name, { code: j.category_code || "", es: j.category_es || "" });
    });
    return [...map.entries()].map(([name, v]) => ({ name, code: v.code, es: v.es })).sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs]);
  const categoryJobs = useMemo(
    () => (form?.category ? jobs.filter((j) => (j.category || "Uncategorized") === form.category) : []),
    [jobs, form]
  );

  const openEdit = (e) => {
    setErr("");
    const job = jobs.find((j) => j.id === e.job_code_id);
    setForm({
      clock_in: toLocalInput(e.clock_in),
      clock_out: toLocalInput(e.clock_out),
      ranch_id: e.ranch_id || "",
      category: job?.category || "Uncategorized",
      job_code_id: e.job_code_id || "",
      breaks: (e.breaks || []).map((b) => ({ kind: b.kind, start: toLocalInput(b.start), end: toLocalInput(b.end) })),
      notes: e.notes || "",
    });
    setEditing(e);
  };

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateBreak = (i, patch) =>
    setForm((f) => ({ ...f, breaks: f.breaks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  const addBreak = (kind) => setForm((f) => ({ ...f, breaks: [...f.breaks, { kind, start: "", end: "" }] }));
  const removeBreak = (i) => setForm((f) => ({ ...f, breaks: f.breaks.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setErr("");
    if (!form.ranch_id) return setErr("Select a ranch.");
    if (!form.job_code_id) return setErr("Select a job code.");
    if (!form.clock_in) return setErr("Clock in time is required.");
    const breaks = form.breaks
      .filter((b) => b.start)
      .map((b) => ({ kind: b.kind, start: fromLocalInput(b.start), end: fromLocalInput(b.end) }));
    setSaving(true);
    try {
      await api.put(`/time-entries/${editing.id}`, {
        clock_in: fromLocalInput(form.clock_in),
        clock_out: fromLocalInput(form.clock_out),
        ranch_id: form.ranch_id,
        job_code_id: form.job_code_id,
        breaks,
        notes: form.notes || null,
      });
      setEditing(null);
      setForm(null);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("ts.title")}</h1>

      {canEdit && (
        <div className="inline-flex border border-stone-200 bg-white p-1" data-testid="ts-view-tabs">
          {[["employee", "Employee Timesheets"], ["equipment", "Equipment Timesheets"], ["block", "By Block"]].map(([v, label]) => (
            <button
              key={v}
              data-testid={`ts-view-${v}`}
              onClick={() => { setView(v); if (v === "equipment") loadEquipment(); else if (v === "block") loadBlocks(); else load(); }}
              className={`h-11 px-5 font-bold uppercase text-xs tracking-wide transition-colors ${
                view === v ? "bg-[#1F3D34] text-white" : "bg-transparent text-stone-600 hover:text-[#1F3D34]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-medium tracking-wide uppercase block mb-1">Start</label>
          <input data-testid="ts-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-12 px-3 border border-stone-200" />
        </div>
        <div>
          <label className="text-xs font-medium tracking-wide uppercase block mb-1">End</label>
          <input data-testid="ts-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-12 px-3 border border-stone-200" />
        </div>
        <button data-testid="ts-load" onClick={loadActive} className="h-12 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5">Load</button>
      </div>

      {view === "block" ? (
        <div className="space-y-3" data-testid="ts-block-view">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <select data-testid="ts-block-ranch" value={blockRanch}
              onChange={(e) => { setBlockRanch(e.target.value); loadBlocks(e.target.value); }}
              className="h-10 px-3 border border-stone-200 bg-white font-medium text-sm">
              <option value="">All Ranches</option>
              {uniqueRanches(ranches).map((r) => <option key={r.id} value={r.id}>{r.ranch_number} · {r.name}</option>)}
            </select>
            <button data-testid="ts-block-export" onClick={downloadBlockXlsx}
              className="h-10 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs">Download XLSX</button>
          </div>
          <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto" data-testid="ts-block-table">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b-2 border-stone-300">
                <tr className="text-left tracking-wide text-xs">
                  <th className="p-3">Ranch</th>
                  <th className="p-3">Block</th>
                  <th className="p-3">Job Code</th>
                  <th className="p-3 text-right">Hours</th>
                  <th className="p-3 text-right">OT+DT</th>
                  <th className="p-3 text-right">Entries</th>
                  <th className="p-3 text-right">Employees</th>
                </tr>
              </thead>
              <tbody>
                {blockData.rows.length === 0 && (<tr><td colSpan={7} className="p-6 text-center text-stone-500 font-bold uppercase">No completed entries in range</td></tr>)}
                {blockData.rows.map((r, i) => {
                  const newBlock = i === 0 || blockData.rows[i - 1].ranch !== r.ranch || blockData.rows[i - 1].block !== r.block;
                  return (
                    <tr key={`${r.ranch}-${r.block}-${r.job_code}`} className={`border-b border-stone-200 ${newBlock ? "" : "text-stone-500"}`} data-testid={`ts-block-row-${i}`}>
                      <td className="p-3 font-bold">{newBlock ? r.ranch : ""}</td>
                      <td className="p-3 font-medium">{newBlock ? r.block : ""}</td>
                      <td className="p-3 font-mono text-xs">{r.job_code}</td>
                      <td className="p-3 font-mono font-bold text-right">{r.hours}</td>
                      <td className="p-3 font-mono text-right">{r.ot_hours}</td>
                      <td className="p-3 font-mono text-right">{r.entries}</td>
                      <td className="p-3 font-mono text-right">{r.employees}</td>
                    </tr>
                  );
                })}
                {blockData.rows.length > 0 && (
                  <tr className="border-t-2 border-stone-300 bg-stone-50" data-testid="ts-block-total">
                    <td className="p-3 font-black uppercase" colSpan={3}>Total</td>
                    <td className="p-3 font-mono font-black text-right">{blockData.total_hours}</td>
                    <td className="p-3" />
                    <td className="p-3 font-mono font-black text-right">{blockData.entry_count}</td>
                    <td className="p-3" />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === "equipment" ? (
        <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto" data-testid="ts-equipment-table">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b-2 border-stone-300">
              <tr className="text-left tracking-wide text-xs">
                <th className="p-3">Date</th>
                <th className="p-3">Employee</th>
                <th className="p-3">Job</th>
                <th className="p-3">Ranch / Client</th>
                <th className="p-3">Equip #</th>
                <th className="p-3">Equipment</th>
                <th className="p-3">Type</th>
                <th className="p-3">Recorded By</th>
                <th className="p-3 text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {equipRows.length === 0 && (<tr><td colSpan={9} className="p-6 text-center text-stone-500 font-bold uppercase">No equipment usage</td></tr>)}
              {equipRows.map((r, i) => (
                <tr key={i} className="border-b border-stone-200" data-testid={`ts-equip-row-${i}`}>
                  <td className="p-3 font-mono">{r.date}</td>
                  <td className="p-3 font-bold">{r.employee_name}</td>
                  <td className="p-3 font-mono text-xs">{r.job_code || "—"}</td>
                  <td className="p-3 text-xs">{r.ranch_name || "—"}</td>
                  <td className="p-3 font-mono text-xs">{r.equipment_number || "—"}</td>
                  <td className="p-3 font-medium">{r.equipment_name}</td>
                  <td className="p-3 text-xs">{r.equipment_type}</td>
                  <td className="p-3 text-xs">{r.recorded_by_name || "—"}</td>
                  <td className="p-3 font-mono font-bold text-right">{r.hours}</td>
                </tr>
              ))}
              {equipRows.length > 0 && (
                <tr className="border-t-2 border-stone-300 bg-stone-50" data-testid="ts-equip-total">
                  <td className="p-3 font-black uppercase" colSpan={8}>Total Equipment Hours</td>
                  <td className="p-3 font-mono font-black text-right">{equipTotal}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b-2 border-stone-300">
            <tr className="text-left tracking-wide text-xs">
              <th className="p-3">Date</th>
              <th className="p-3">Employee</th>
              <th className="p-3">Ranch</th>
              <th className="p-3">Job</th>
              <th className="p-3 font-mono">In</th>
              <th className="p-3 font-mono">Out</th>
              <th className="p-3">Reg</th>
              <th className="p-3">OT</th>
              <th className="p-3">DT</th>
              <th className="p-3">Total</th>
              <th className="p-3">Recorded By</th>
              <th className="p-3">Flags</th>
              {canEdit && <th className="p-3"></th>}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (<tr><td colSpan={canEdit ? 13 : 12} className="p-6 text-center text-stone-500 font-bold uppercase">No entries</td></tr>)}
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-stone-200" data-testid={`ts-row-${e.id}`}>
                <td className="p-3 font-mono">{e.work_date}</td>
                <td className="p-3 font-bold">{e.employee_name}</td>
                <td className="p-3 text-xs">{e.ranch_name || "—"}</td>
                <td className="p-3 font-mono text-xs">{e.job_code || "—"}</td>
                <td className="p-3 font-mono text-xs">{e.clock_in ? new Date(e.clock_in).toLocaleTimeString() : "—"}</td>
                <td className="p-3 font-mono text-xs">{e.clock_out ? new Date(e.clock_out).toLocaleTimeString() : "—"}</td>
                <td className="p-3 font-mono">{e.regular_hours}</td>
                <td className="p-3 font-mono">{e.overtime_hours}</td>
                <td className="p-3 font-mono">{e.doubletime_hours}</td>
                <td className="p-3 font-mono font-bold">{e.total_hours}</td>
                <td className="p-3 text-xs">{e.recorded_by_name || "—"}</td>
                <td className="p-3 text-xs space-x-1">
                  {e.meal_break_penalty && <span className="bg-red-100 border border-red-600 text-red-700 px-2 py-0.5 font-bold uppercase">Meal</span>}
                  {e.rest_break_penalty && <span className="bg-red-100 border border-red-600 text-red-700 px-2 py-0.5 font-bold uppercase">Rest</span>}
                  {e.seventh_day && <span className="bg-[#D8C7A1] border border-stone-300 px-2 py-0.5 font-bold uppercase">7th</span>}
                  {e.edited_by && <span className="bg-stone-100 border border-stone-300 px-2 py-0.5 font-bold uppercase" title={`Edited by ${e.edited_by_name}`}>Edited</span>}
                  {e.locked && <span className="bg-stone-800 text-white px-2 py-0.5 font-bold uppercase">Locked</span>}
                </td>
                {canEdit && (
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      <button
                        data-testid={`ts-edit-${e.id}`}
                        onClick={() => openEdit(e)}
                        className="h-9 px-3 border border-stone-300 font-bold uppercase text-xs flex items-center gap-1 hover:bg-stone-50"
                      >
                        <PencilSimple weight="bold" size={14} /> {t("ts.edit")}
                      </button>
                      {user?.role === "admin" && (
                        <button
                          data-testid={`ts-delete-${e.id}`}
                          onClick={() => removeEntry(e)}
                          className="h-9 px-2.5 border border-red-300 text-red-600 font-bold uppercase text-xs flex items-center gap-1 hover:bg-red-50"
                          title="Delete entry"
                        >
                          <Trash weight="bold" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {editing && form && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" data-testid="ts-edit-modal">
          <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/20 w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
                {t("ts.edit_title")}
              </h3>
              <button data-testid="ts-edit-close" onClick={() => { setEditing(null); setForm(null); }} className="h-10 w-10 border border-stone-200 flex items-center justify-center"><X weight="bold" /></button>
            </div>
            <p className="text-xs font-mono text-stone-500 mb-4">{editing.employee_name} · {editing.work_date}</p>

            {editing.locked && (
              <div className="bg-[#D8C7A1] border border-stone-300 px-3 py-2 text-xs font-bold uppercase mb-4" data-testid="ts-edit-locked-warning">
                This card is locked (approved). Saving will override the approved values.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide block mb-1">Clock In *</label>
                <input data-testid="ts-edit-clockin" type="datetime-local" value={form.clock_in} onChange={(e) => updateForm({ clock_in: e.target.value })} className="w-full h-12 px-3 border border-stone-200" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide block mb-1">Clock Out</label>
                <input data-testid="ts-edit-clockout" type="datetime-local" value={form.clock_out} onChange={(e) => updateForm({ clock_out: e.target.value })} className="w-full h-12 px-3 border border-stone-200" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide block mb-1">Ranch *</label>
                <select data-testid="ts-edit-ranch" value={repRanchId(ranches, form.ranch_id)} onChange={(e) => updateForm({ ranch_id: e.target.value })} className="w-full h-12 px-3 border border-stone-200 font-medium">
                  <option value="">— Select Ranch —</option>
                  {uniqueRanches(ranches).map((r) => (
                    <option key={r.id} value={r.id}>{r.ranch_number} · {r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide block mb-1">Category *</label>
                <select data-testid="ts-edit-category" value={form.category} onChange={(e) => updateForm({ category: e.target.value, job_code_id: "" })} className="w-full h-12 px-3 border border-stone-200 font-medium">
                  <option value="">— Select Category —</option>
                  {categories.map((c) => (
                    <option key={c.name} value={c.name}>{c.code ? `${c.code} · ` : ""}{bilingual(c.name, c.es, lang)}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wide block mb-1">Job Code *</label>
                <select data-testid="ts-edit-job" value={form.job_code_id} disabled={!form.category} onChange={(e) => updateForm({ job_code_id: e.target.value })} className="w-full h-12 px-3 border border-stone-200 font-medium disabled:bg-stone-100">
                  <option value="">{form.category ? "— Select Job Code —" : "— Select a category first —"}</option>
                  {categoryJobs.map((j) => (
                    <option key={j.id} value={j.id}>{j.job_code || j.code} · {bilingual(j.job || j.description, j.job_es, lang)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase tracking-wide">Breaks</label>
                <div className="flex gap-2">
                  <button data-testid="ts-edit-add-meal" onClick={() => addBreak("meal")} className="h-9 px-3 border border-stone-300 text-xs font-bold uppercase flex items-center gap-1"><Plus weight="bold" size={12} /> Meal</button>
                  <button data-testid="ts-edit-add-rest" onClick={() => addBreak("rest")} className="h-9 px-3 border border-stone-300 text-xs font-bold uppercase flex items-center gap-1"><Plus weight="bold" size={12} /> Rest</button>
                </div>
              </div>
              {form.breaks.length === 0 && <p className="text-xs text-stone-500">No breaks recorded.</p>}
              <div className="space-y-2">
                {form.breaks.map((b, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-2 border border-stone-200 p-2" data-testid={`ts-edit-break-${i}`}>
                    <span className={`px-2 py-1 text-xs font-bold uppercase ${b.kind === "meal" ? "bg-[#1F3D34] text-white" : "bg-[#7C9673] text-white"}`}>{b.kind}</span>
                    <div>
                      <label className="text-[10px] uppercase block">Start</label>
                      <input type="datetime-local" value={b.start} onChange={(e) => updateBreak(i, { start: e.target.value })} className="h-10 px-2 border border-stone-200 text-xs" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase block">End</label>
                      <input type="datetime-local" value={b.end} onChange={(e) => updateBreak(i, { end: e.target.value })} className="h-10 px-2 border border-stone-200 text-xs" />
                    </div>
                    <button onClick={() => removeBreak(i)} className="h-10 w-10 border border-red-300 text-red-600 flex items-center justify-center"><Trash weight="bold" size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wide block mb-1">Notes</label>
              <textarea data-testid="ts-edit-notes" value={form.notes} onChange={(e) => updateForm({ notes: e.target.value })} rows={2} className="w-full p-3 border border-stone-200 text-sm" />
            </div>

            {err && <div className="mt-3 bg-red-50 border border-red-300 text-red-700 px-3 py-2 text-sm font-medium" data-testid="ts-edit-error">{err}</div>}

            <div className="mt-5 flex gap-3">
              <button data-testid="ts-edit-save" disabled={saving} onClick={save} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 disabled:opacity-50">{saving ? t("common.loading") : t("ts.save")}</button>
              <button data-testid="ts-edit-cancel" onClick={() => { setEditing(null); setForm(null); }} className="h-12 px-6 border border-stone-300 font-bold uppercase">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
